/**
 * Invite campaigns — the ?inv=<code> a Pardot invitation link carries. Anyone
 * who registers through that link is proposed for the campaign's retailer.
 * With `retailerId`, only that retailer's codes are listed and new ones
 * default to it (the People tab of the Retailers workspace). Admin-only.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Megaphone, Plus, Copy, Trash2 } from 'lucide-react';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import {
  fetchInviteCampaigns, saveInviteCampaign, deleteInviteCampaign,
  type InviteCampaign, type RetailerOption,
} from '../../../services/adminRetailerMembersApi';

const fmtDate = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—');

export const InviteCampaigns: React.FC<{ canEdit: boolean; retailerId?: number }> = ({ canEdit, retailerId: onlyRetailer }) => {
  const [allCampaigns, setAllCampaigns] = useState<InviteCampaign[] | null>(null);
  const campaigns = useMemo(() => allCampaigns === null ? null : onlyRetailer ? allCampaigns.filter(c => c.retailerId === onlyRetailer) : allCampaigns, [allCampaigns, onlyRetailer]);
  const [retailers, setRetailers] = useState<RetailerOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState('');
  const [retailerId, setRetailerId] = useState<number | ''>(onlyRetailer ?? '');
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [del, setDel] = useState<InviteCampaign | null>(null);

  useEffect(() => {
    fetchInviteCampaigns()
      .then(d => { setAllCampaigns(d.campaigns); setRetailers(d.retailers); })
      .catch(e => { setAllCampaigns([]); setError(e instanceof Error ? e.message : 'Failed to load campaigns'); });
  }, []);

  const joinUrl = (c: string) => `${window.location.origin}/retailers/join?inv=${encodeURIComponent(c)}&email=`;

  const run = async (fn: () => Promise<{ campaigns: InviteCampaign[] }>, fail: string) => {
    setSaving(true); setError(null);
    try { setAllCampaigns((await fn()).campaigns); }
    catch (e) { setError(e instanceof Error ? e.message : fail); }
    finally { setSaving(false); }
  };

  const add = () => run(async () => {
    const d = await saveInviteCampaign({ code: code.trim(), retailerId: retailerId === '' ? null : retailerId, label: label.trim() || null, isActive: true, expiresAt: expiresAt || null });
    setCode(''); setLabel(''); setExpiresAt(''); setRetailerId(onlyRetailer ?? '');
    return d;
  }, 'Could not save the campaign');

  const toggle = (c: InviteCampaign) => run(() => saveInviteCampaign({ id: c.id, code: c.code, retailerId: c.retailerId, label: c.label, isActive: !c.isActive, expiresAt: c.expiresAt }), 'Could not update the campaign');

  const copy = async (c: string) => {
    try { await navigator.clipboard.writeText(joinUrl(c)); setCopied(c); setTimeout(() => setCopied(null), 1500); } catch { /* clipboard blocked */ }
  };

  return (
    <div className="bg-white rounded-3 border p-3 mt-2">
      <p className="text-muted small mb-3">
        A campaign code is what a Pardot invitation link carries: <code>/retailers/join?inv=CODE&amp;email={'{{Recipient.Email}}'}</code>.
        Anyone who registers through that link is proposed for the campaign&apos;s retailer and lands in the People tab waiting for approval.
        {!onlyRetailer && ' Leave the retailer blank for a general invitation.'}
      </p>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {campaigns === null ? (
        <div className="text-muted small"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
      ) : (
        <table className="table table-sm align-middle mb-3 small">
          <thead className="table-light">
            <tr><th>Code</th>{!onlyRetailer && <th>Retailer</th>}<th>Label</th><th>Expires</th><th>Active</th><th className="text-end">Link</th></tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && <tr><td colSpan={6} className="text-muted text-center py-3">No invitation codes yet.</td></tr>}
            {campaigns.map(c => (
              <tr key={c.id} className={c.isActive ? '' : 'text-muted'}>
                <td className="font-monospace">{c.code}</td>
                {!onlyRetailer && <td>{c.retailerName ?? <span className="text-muted">— general —</span>}</td>}
                <td>{c.label ?? ''}</td>
                <td>{c.expiresAt ? fmtDate(c.expiresAt) : 'never'}</td>
                <td>
                  <div className="form-check form-switch m-0">
                    <input type="checkbox" className="form-check-input" role="switch" checked={c.isActive} disabled={!canEdit || saving} onChange={() => void toggle(c)} aria-label={`${c.code} active`} />
                  </div>
                </td>
                <td className="text-end">
                  <div className="d-inline-flex gap-1">
                    <button type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" onClick={() => void copy(c.code)} title={joinUrl(c.code)}>
                      <Copy size={12} /> {copied === c.code ? 'Copied' : 'Copy link'}
                    </button>
                    {canEdit && <button type="button" className="btn btn-outline-danger btn-sm" disabled={saving} onClick={() => setDel(c)} title="Delete campaign"><Trash2 size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canEdit && (
        <div className="row g-2 align-items-end">
          <div className="col-sm-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="inv-code">Code</label>
            <input id="inv-code" className="form-control form-control-sm font-monospace" placeholder="HH-FALL-2026" maxLength={64} value={code} onChange={e => setCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} />
          </div>
          {!onlyRetailer && (
            <div className="col-sm-3">
              <label className="form-label small fw-semibold mb-1" htmlFor="inv-retailer">Retailer</label>
              <select id="inv-retailer" className="form-select form-select-sm" value={retailerId} onChange={e => setRetailerId(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">— general —</option>
                {retailers.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          <div className={onlyRetailer ? 'col-sm-5' : 'col-sm-3'}>
            <label className="form-label small fw-semibold mb-1" htmlFor="inv-label">Label</label>
            <input id="inv-label" className="form-control form-control-sm" placeholder="Fall dealer mailing" maxLength={255} value={label} onChange={e => setLabel(e.target.value)} />
          </div>
          <div className="col-sm-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="inv-exp">Expires</label>
            <input id="inv-exp" type="date" className="form-control form-control-sm" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
          </div>
          <div className="col-sm-2 d-grid">
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center gap-1" disabled={saving || !code.trim()} onClick={() => void add()}><Plus size={14} /> Add code</button>
          </div>
        </div>
      )}

      <ConfirmModal open={!!del} title={`Delete code ${del?.code}?`} confirmLabel="Delete" variant="danger" busy={saving} onCancel={() => setDel(null)}
        onConfirm={() => { const c = del; setDel(null); if (c) void run(() => deleteInviteCampaign(c.id), 'Could not delete the campaign'); }}>
        <p className="mb-0">Existing links with this code will no longer propose a retailer.</p>
      </ConfirmModal>
    </div>
  );
};

export default InviteCampaigns;
